import React, {useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";

import {Button} from "@mui/material";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import {greyishBlue600, orange600, red600} from "assets/jss/main";

import {formatDate, formatDateTime} from "views/personalShopper/components/formatters";

import EraseDialog from "./EraseDialog";
import SideCard from "./SideCard";

PrivacyCard.propTypes = {
    conversation: PropTypes.shape({
        id: PropTypes.number,
        waId: PropTypes.string,
        marketingOptOut: PropTypes.bool,
        erasureRequestedAt: PropTypes.string,
        retention: PropTypes.shape({
            expiresAt: PropTypes.string,
            expiringSoon: PropTypes.bool,
        }),
    }).isRequired,
    onSetMarketingOptOut: PropTypes.func.isRequired,
    onExport: PropTypes.func.isRequired,
    onErase: PropTypes.func.isRequired,
};

/**
 * What the GDPR gives the shopper, as the Marketplace sees it: when the sheet goes unless
 * they write again (Sylius' retention purge), whether they objected to marketing (set or
 * lifted from here, through Sylius), an erasure they asked for in the chat and still have to
 * confirm, the export of everything held about them (right of access, a JSON file the member
 * downloads) and the erasure itself (right of erasure), both done by Sylius where the data is.
 */
function PrivacyCard({conversation, onSetMarketingOptOut, onExport, onErase}) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [eraseOpen, setEraseOpen] = useState(false);
    const retention = conversation.retention ?? {};

    const run = (action) => {
        setSaving(true);
        setError(null);

        return action()
            .catch((requestError) => setError(requestError))
            .finally(() => setSaving(false));
    };

    const download = () => run(() => onExport().then((data) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], {type: "application/json"});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `personal-shopper-${conversation.waId ?? conversation.id}.json`;
        link.click();
        URL.revokeObjectURL(url);
    }));

    const toggle = () => run(() => onSetMarketingOptOut(!conversation.marketingOptOut));

    return (
        <SideCard titleId="personalShopper.privacy.title">
            <div style={{fontSize: "13px", display: "grid", gap: "8px"}}>
                {retention.expiresAt && (
                    <div style={{color: retention.expiringSoon ? orange600 : greyishBlue600}}>
                        <FormattedMessage id="personalShopper.privacy.retention" values={{date: formatDate(retention.expiresAt)}} />
                        {retention.expiringSoon && (
                            <>
                                {" "}
                                <FormattedMessage id="personalShopper.privacy.expiringSoon" />
                            </>
                        )}
                    </div>
                )}
                {conversation.erasureRequestedAt && (
                    <div style={{color: red600}}>
                        <FormattedMessage id="personalShopper.privacy.erasureRequested" values={{date: formatDateTime(conversation.erasureRequestedAt)}} />
                    </div>
                )}
                <div style={{color: conversation.marketingOptOut ? red600 : greyishBlue600}}>
                    <FormattedMessage id={conversation.marketingOptOut ? "personalShopper.privacy.optOut.on" : "personalShopper.privacy.optOut.off"} />
                </div>
                <div>
                    <Button size="small" variant="outlined" color={conversation.marketingOptOut ? "inherit" : "warning"} onClick={toggle} disabled={saving}>
                        <FormattedMessage id={conversation.marketingOptOut ? "personalShopper.privacy.optOut.lift" : "personalShopper.privacy.optOut.set"} />
                    </Button>
                </div>
                <div style={{display: "flex", gap: "8px", flexWrap: "wrap"}}>
                    <Button size="small" variant="outlined" color="inherit" startIcon={<FileDownloadOutlinedIcon />} onClick={download} disabled={saving} sx={{textTransform: "none"}}>
                        <FormattedMessage id="personalShopper.privacy.export" />
                    </Button>
                    <Button size="small" variant="outlined" color="error" startIcon={<DeleteForeverOutlinedIcon />} onClick={() => setEraseOpen(true)} disabled={saving} sx={{textTransform: "none"}}>
                        <FormattedMessage id="personalShopper.privacy.erase" />
                    </Button>
                </div>
                <div style={{color: greyishBlue600, fontSize: "12px"}}>
                    <FormattedMessage id="personalShopper.privacy.export.help" />
                </div>
                {error && <div style={{color: red600}}>{error.message}</div>}
                <div style={{color: greyishBlue600, fontSize: "12px"}}>
                    <FormattedMessage id="personalShopper.privacy.rights" />
                </div>
            </div>
            <EraseDialog open={eraseOpen} busy={saving} onConfirm={() => run(onErase)} onClose={() => setEraseOpen(false)} />
        </SideCard>
    );
}

export default PrivacyCard;
